import { Accordion } from '@distribusion/owl-frontend-ui'
import { useFormikContext } from 'formik'
import React, { PropsWithChildren, ReactElement, ReactNode, useMemo } from 'react'

import ancillaryUtils from '@lib/ancillary'
import { useTranslation } from '@lib/i18n'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import type { SeatSelectionProps } from '@pages/Checkout/SeatSelection/index'
import Seats from '@pages/Checkout/SeatSelection/NewSeats'
import PluginSlot from '@plugins/Slot'
import { useSettings } from '@queries/settings'

import '@pages/Checkout/SeatSelection/legacy.scss'

const FallbackTitle: React.FC<{ title: ReactNode; className?: string; index: number; dataTag?: string }> = ({
  className,
  title,
  dataTag,
}) => (
  <h3 className={className} data-tag={dataTag}>
    {title}
  </h3>
)

function Title({ className, children }: PropsWithChildren<{ className?: string }>) {
  return (
    <PluginSlot
      path="checkout.fieldsetHeader"
      props={{
        title: children,
        className,
        index: 3,
        dataTag: 'checkout-seat-selection-title',
      }}
      FallbackComponent={FallbackTitle}
    />
  )
}

const MandatorySeatSelection = ({ onSeatsError, isLoading }: SeatSelectionProps): ReactElement => {
  const { t } = useTranslation()
  const [{ reservation }] = useSettings()

  return (
    <div className="extras seats cell-12 column">
      <Title className="mb-5">{t('extras.seats.title')}</Title>
      <Seats isConnectionsLoading={isLoading} onError={onSeatsError} enabled={!reservation.enabled} />
    </div>
  )
}

const OptionalSeatSelection = ({ onSeatsError, isLoading }: SeatSelectionProps): ReactElement => {
  const { t } = useTranslation()
  const [{ reservation }] = useSettings()

  return (
    <Accordion titleComponent={Title} title={t('extras.seats.title')} divider={false} renderChildrenWhileClosed>
      <div className="extras seats cell-12 column">
        <Seats isConnectionsLoading={isLoading} onError={onSeatsError} enabled={!reservation.enabled} />
      </div>
    </Accordion>
  )
}

const SeatSelection = (props: SeatSelectionProps): ReactElement => {
  const [{ seatSelection }] = useSettings()
  const {
    values: { vacancy },
  } = useFormikContext<CheckoutFormData>()
  const seatAncillaries = useMemo(() => ancillaryUtils.getByCategory('SEAT', vacancy?.ancillaries), [vacancy])
  const isSomeSeatMandatory = useMemo(
    () => seatAncillaries.some(seat => ancillaryUtils.isMandatory(seat)),
    [seatAncillaries],
  )

  const Component = seatSelection.mandatory || isSomeSeatMandatory ? MandatorySeatSelection : OptionalSeatSelection

  return <Component {...props} />
}

export default SeatSelection
